
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, DollarSign, PackageCheck, ShoppingBag, Store, Truck } from 'lucide-react';
import ShopifyIntegrationSettings from './ShopifyIntegrationSettings';
import WooCommerceIntegrationSettings from './WooCommerceIntegrationSettings';
import BigCommerceIntegrationSettings from './BigCommerceIntegrationSettings';

interface EcommerceSettingsTabProps {
  onSaved: () => Promise<void>;
  initialData?: {
    shopifyConnected?: boolean;
    wooCommerceConnected?: boolean;
    bigCommerceConnected?: boolean;
    autoReferralEnabled?: boolean;
    commissionRate?: number;
  };
}

const EcommerceSettingsTab: React.FC<EcommerceSettingsTabProps> = ({
  onSaved,
  initialData = {}
}) => {
  const [activeTab, setActiveTab] = useState('platforms');
  const [platformTab, setPlatformTab] = useState('shopify');
  
  const {
    shopifyConnected = false,
    wooCommerceConnected = false,
    bigCommerceConnected = false,
    autoReferralEnabled = false,
    commissionRate = 10
  } = initialData;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">E-commerce Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4 grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="platforms" className="flex items-center">
              <Store className="mr-2 h-4 w-4" />
              <span>Platforms</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center">
              <PackageCheck className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="platforms">
            <Tabs 
              value={platformTab} 
              onValueChange={setPlatformTab} 
              className="w-full"
            >
              <TabsList className="mb-4 grid w-full max-w-md grid-cols-3">
                <TabsTrigger value="shopify" className="flex items-center">
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  <span>Shopify</span>
                </TabsTrigger>
                <TabsTrigger value="woocommerce" className="flex items-center">
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  <span>WooCommerce</span>
                </TabsTrigger>
                <TabsTrigger value="bigcommerce" className="flex items-center">
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  <span>BigCommerce</span>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="shopify">
                <ShopifyIntegrationSettings 
                  initialConnected={shopifyConnected}
                  onSaved={onSaved} 
                />
              </TabsContent>
              
              <TabsContent value="woocommerce">
                <WooCommerceIntegrationSettings 
                  initialConnected={wooCommerceConnected}
                  onSaved={onSaved} 
                />
              </TabsContent>
              
              <TabsContent value="bigcommerce">
                <BigCommerceIntegrationSettings 
                  initialConnected={bigCommerceConnected}
                  onSaved={onSaved} 
                />
              </TabsContent>
            </Tabs>
          </TabsContent>
          
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Truck className="mr-2 h-5 w-5" />
                  Automatic Referral Creation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col space-y-2 p-4 border rounded-md">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium">Automatic Referral Creation</h3>
                      <p className="text-sm text-muted-foreground">
                        When enabled, new referrals will be automatically created when customers make purchases through your connected e-commerce platforms.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-2 p-4 border rounded-md">
                  <div className="flex items-start space-x-2">
                    <DollarSign className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium">Commission Settings</h3>
                      <p className="text-sm text-muted-foreground">
                        Configure the default commission rate for e-commerce referrals. This can be overridden on a per-product basis.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default EcommerceSettingsTab;
